/**
 * Policy Reading Room — the read-only face of Policy Management for
 * department users (2026-07-20 access model). PUBLISHED policies only; the
 * server (routes/ppm.js /library routes) is the authoritative filter, so
 * nothing in draft, review, or analysis ever reaches this page.
 *
 * Mirrors the compliance register's reading experience (filter bar, table
 * with status / category / owner / next review, detail modal with View and
 * Download) with ZERO edit affordances. The Radar, Mandate-drift, conflict,
 * and Counsel surfaces are compliance-only and are not rendered here at all.
 *
 * Not to be confused with PolicyLibrary.jsx — the compliance workbench's
 * editable policy register.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { takePolicyParam } from './policyParam.js';
import { PolicyReviewSection } from '../Workspace';

const API = process.env.REACT_APP_API_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}

function formatBytes(n) {
  const num = Number(n);
  if (!num || num <= 0) return '';
  if (num < 1024 * 1024) return `${Math.max(1, Math.round(num / 1024))} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PolicyReadingRoom() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter bar state
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  // Detail modal state
  const [openPolicy, setOpenPolicy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // ?policy=<id> from a link (Readiness "Open the policy"): open it once the
  // published list is here; say so when it is not published.
  const [wanted] = useState(() => takePolicyParam());
  const [notPublished, setNotPublished] = useState(false);
  useEffect(() => {
    if (!wanted || loading) return;
    const p = policies.find((x) => String(x.id) === String(wanted));
    if (p) setOpenPolicy(p); else setNotPublished(true);
  }, [wanted, loading, policies]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/ppm/library`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d?.success) setPolicies(d.policies || []);
        else setError(d?.message || 'Failed to load the policy library.');
      })
      .catch(e => !cancelled && setError(e?.message || 'Failed to load the policy library.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!openPolicy) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    fetch(`${API}/api/ppm/library/${openPolicy.id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.success) setDetail(d); })
      .catch(() => {})
      .finally(() => !cancelled && setDetailLoading(false));
    return () => { cancelled = true; };
  }, [openPolicy]);

  // Filter options derived from the data itself (published policies only).
  const categories  = useMemo(() => [...new Set(policies.map(p => p.category).filter(Boolean))].sort(), [policies]);
  const departments = useMemo(() => [...new Set(policies.map(p => p.department_name).filter(Boolean))].sort(), [policies]);
  const owners      = useMemo(() => [...new Set(policies.map(p => p.owner_name).filter(Boolean))].sort(), [policies]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => policies.filter(p => {
    if (categoryFilter && p.category !== categoryFilter) return false;
    if (departmentFilter && p.department_name !== departmentFilter) return false;
    if (ownerFilter && p.owner_name !== ownerFilter) return false;
    if (q && !(`${p.policy_number} ${p.title} ${p.description || ''} ${p.category || ''} ${p.department_name || ''} ${p.owner_name || ''}`.toLowerCase().includes(q))) return false;
    return true;
  }), [policies, q, categoryFilter, departmentFilter, ownerFilter]);

  const hasActiveFilters = q || categoryFilter || departmentFilter || ownerFilter;

  const reviewBadge = (p) => {
    if (!p.next_review_date) return <span className="text-slate-400">—</span>;
    const overdue = new Date(p.next_review_date) < new Date();
    return (
      <span className={overdue ? 'text-amber-700' : 'text-slate-600'}>
        {formatDate(p.next_review_date)}
      </span>
    );
  };

  return (
    /* h-full, not min-h-full: the PPM shell wraps this view in
       overflow-hidden, so this root must BE the viewport height for its own
       overflow-y-auto to engage. With min-h-full the root grew with its
       content, nothing ever scrolled, and department users were stuck at the
       first screen (live-found 2026-08-20 — compliance users were unaffected
       because their workbench manages its own inner scroll). */
    <div className="h-full bg-slate-50 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-10 pb-20">

        {/* Policies waiting on this user come first: the same list the
            review email opens, with confirm, replace, delegate and attest. */}
        <PolicyReviewSection />

        <header className="mb-6">
          <h1 className="text-lg font-bold text-slate-800 tracking-tight">Policy Library</h1>
          <p className="text-slate-400 text-xs mt-0.5">
            {policies.length} current {policies.length === 1 ? 'policy' : 'policies'} · read-only · questions go to your compliance team
          </p>
        </header>

        {/* ── Filter bar ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5 flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search policies…"
            className="flex-1 min-w-[220px] px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:outline-none"
          />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 focus:border-blue-400 focus:outline-none">
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 focus:border-blue-400 focus:outline-none">
            <option value="">All departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 focus:border-blue-400 focus:outline-none">
            <option value="">All owners</option>
            {owners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => { setSearch(''); setCategoryFilter(''); setDepartmentFilter(''); setOwnerFilter(''); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* ── Register table ─────────────────────────────────────────── */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-slate-500">Loading policies…</div>
        ) : error ? (
          <div className="bg-white rounded-2xl border border-amber-200 p-6 text-sm text-amber-800">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <p className="text-[15px] font-medium text-slate-900">{hasActiveFilters ? 'No policies match your filters.' : 'No published policies yet.'}</p>
            {!hasActiveFilters && <p className="text-sm text-slate-500 mt-1">Policies appear here once your compliance team publishes them.</p>}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-[11px] uppercase tracking-[0.05em] text-slate-500">
                    <th className="px-5 py-3 font-semibold">Policy</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    {/* Category column HIDDEN (not removed) per operator decision
                        2026-07-22: the business files by department. */}
                    <th className="px-4 py-3 font-semibold">Dept. Owner</th>
                    <th className="px-4 py-3 font-semibold">Policy Owner</th>
                    <th className="px-4 py-3 font-semibold">Next Review</th>
                    <th className="px-4 py-3 font-semibold text-right">File</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(p => (
                    <tr key={p.id} onClick={() => setOpenPolicy(p)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors">
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-slate-900">{p.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{p.policy_number}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">Active</span>
                      </td>
                      {false && <td className="px-4 py-3.5 text-slate-600 capitalize">{p.category || '—'}</td>}
                      <td className="px-4 py-3.5 text-slate-600">{p.department_name || '—'}</td>
                      <td className="px-4 py-3.5 text-slate-600">{p.owner_name || '—'}</td>
                      <td className="px-4 py-3.5">{reviewBadge(p)}</td>
                      <td className="px-4 py-3.5 text-right text-slate-400 text-xs">{Number(p.file_count) > 0 ? `${p.file_count} ${Number(p.file_count) === 1 ? 'file' : 'files'}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {notPublished && (
        <div className="mx-6 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-center justify-between gap-3">
          <span>The policy you were sent to is not in the published library. Ask compliance for it.</span>
          <button type="button" className="text-xs font-semibold underline" onClick={() => setNotPublished(false)}>Dismiss</button>
        </div>
      )}
      {/* ── Detail modal ─────────────────────────────────────────────── */}
      {openPolicy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-slate-900/40" onClick={() => setOpenPolicy(null)} style={{ backdropFilter: 'blur(4px)' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full max-w-2xl max-h-[85vh] flex flex-col">

            <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs text-slate-400">{openPolicy.policy_number}</p>
                <h2 className="text-[16px] font-semibold text-slate-900 mt-0.5">{openPolicy.title}</h2>
              </div>
              <button type="button" onClick={() => setOpenPolicy(null)}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none flex-shrink-0" aria-label="Close">×</button>
            </div>

            <div className="px-6 py-4 overflow-y-auto min-h-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.06em] text-slate-400 font-semibold">Status</p>
                  <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">Active</span>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.06em] text-slate-400 font-semibold">Category</p>
                  <p className="text-sm text-slate-700 mt-1 capitalize">{openPolicy.category || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.06em] text-slate-400 font-semibold">Policy Owner</p>
                  <p className="text-sm text-slate-700 mt-1">{openPolicy.owner_name || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.06em] text-slate-400 font-semibold">Next Review</p>
                  <p className="text-sm text-slate-700 mt-1">{formatDate(openPolicy.next_review_date)}</p>
                </div>
              </div>

              {openPolicy.description && (
                <p className="text-sm text-slate-600 mb-4">{openPolicy.description}</p>
              )}

              {detailLoading ? (
                <p className="text-sm text-slate-500">Loading policy…</p>
              ) : detail?.policy?.id === openPolicy.id ? (
                <>
                  {(detail.files || []).length > 0 && (
                    <div className="mb-4">
                      <p className="text-[10px] uppercase tracking-[0.06em] text-slate-400 font-semibold mb-2">Documents</p>
                      <div className="space-y-2">
                        {detail.files.map(f => (
                          <div key={f.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                            <div className="min-w-0">
                              <p className="text-sm text-slate-800 truncate">{f.file_name}</p>
                              <p className="text-[11px] text-slate-400">{formatBytes(f.size_bytes)}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <a href={`${API}/api/ppm/library/${openPolicy.id}/file/${f.id}?disposition=inline`}
                                target="_blank" rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-slate-300 transition">
                                View
                              </a>
                              <a href={`${API}/api/ppm/library/${openPolicy.id}/file/${f.id}`}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-slate-300 transition">
                                Download
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {detail.version?.content && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.06em] text-slate-400 font-semibold mb-2">Policy Text</p>
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 max-h-[320px] overflow-y-auto">
                        <pre className="whitespace-pre-wrap font-[Georgia,serif] text-[13px] leading-relaxed text-slate-800">{detail.version.content}</pre>
                      </div>
                    </div>
                  )}

                  {!detail.version?.content && (detail.files || []).length === 0 && (
                    <p className="text-sm text-slate-500">This policy has no readable content attached yet. Ask your compliance team.</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">Could not load this policy. Close and try again.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
